import * as angular from "angular";

export interface IPieDataSeries {
    label?: string;
    value: number;
    color: string;
}

export class CanvasService {

    private singleUseCanvasStore = new Map<string, HTMLCanvasElement>();
    private imageCanvasStore = new Map<string, HTMLCanvasElement>();

    private blobCache = new Map<string, string>();
    private blobPromiseCache = new Map<string, angular.IPromise<string>>();

    static $inject = ["$q", "$window"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $window: angular.IWindowService
    ) {
    }

    getBlobCache(uniqueCacheCode: string): string {
        return this.blobCache.get(uniqueCacheCode);
    }

    getBlobCachePromise(uniqueCacheCode: string): angular.IPromise<string> {
        return this.blobPromiseCache.get(uniqueCacheCode);
    }

    getBlobUrlPromise(canvas: HTMLCanvasElement, uniqueCacheCode: string = null): angular.IPromise<string> {
        var url = "";
        var defer = this.$q.defer<string>();

        //console.log("blub uetlet");

        this.blobPromiseCache.set(uniqueCacheCode, defer.promise);

        canvas.toBlob(blob => {
            url = URL.createObjectURL(blob);

            if (uniqueCacheCode) {
                if (this.blobCache.has(uniqueCacheCode)) {
                    console.log("Cache already exists for", uniqueCacheCode, this.blobCache.get(uniqueCacheCode))
                }

                this.blobCache.set(uniqueCacheCode, url);
            }

            defer.resolve(url);
        });

        return defer.promise;
    }

    rotateContext(context: CanvasRenderingContext2D, angle: number) {
        context.rotate(angle * (Math.PI / 180));
    }

    drawRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number | { tl: number, tr: number, bl: number, br: number }) {
        if (typeof radius === 'undefined') {
            radius = 5;
        }

        if (typeof radius === 'number') {
            radius = { tl: radius, tr: radius, br: radius, bl: radius };
        }
        else {
            var defaultRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
            for (var side in defaultRadius) {
                radius[side] = radius[side] || defaultRadius[side];
            }
        }

        ctx.beginPath();
        ctx.moveTo(x + radius.tl, y);
        ctx.lineTo(x + width - radius.tr, y);
        ctx.arcTo(x + width, y, x + width, y + radius.tr, radius.tr);
        ctx.lineTo(x + width, y + height - radius.br);
        ctx.arcTo(x + width, y + height, x + width - radius.br, y + height, radius.br);
        ctx.lineTo(x + radius.bl, y + height);
        ctx.arcTo(x, y + height, x, y + height - radius.bl, radius.bl);
        ctx.lineTo(x, y + radius.tl);
        ctx.arcTo(x, y, x + radius.tl, y, radius.tl);
        ctx.closePath();
    }

    getCachedImageCanvas(width: number, height: number, ...identifiers: string[]): HTMLCanvasElement {
        const identifier = this.createCachedImageIdentifier(width, height, ...identifiers);

        //console.log("gcic", identifier, this.imageCanvasStore.length);

        if (this.imageCanvasStore.has(identifier)) {
            return this.imageCanvasStore.get(identifier);
        }

        return null;
    }

    createCachedImageCanvas(width: number, height: number, ...identifiers: string[]): HTMLCanvasElement {
        const identifier = this.createCachedImageIdentifier(width, height, ...identifiers);

        var canvas = this.createHiDPICanvas(width, height);
        this.imageCanvasStore.set(identifier, canvas);

        return canvas;
    }

    destroyCachedImageCanvases() {
        this.imageCanvasStore.forEach((canvas) => {
            canvas.remove();
        });

        this.imageCanvasStore.clear();
    }

    getSingleUseCanvas(width: number, height: number): HTMLCanvasElement {
        const arrayPosition = `${width}-${height}`;

        if (this.singleUseCanvasStore.has(arrayPosition)) {
            var cachedCanvas = this.singleUseCanvasStore.get(arrayPosition);

            const context = cachedCanvas.getContext("2d");
            context.clearRect(0, 0, width, height);

            return cachedCanvas;
        }

        var canvas = this.createHiDPICanvas(width, height);

        this.singleUseCanvasStore.set(arrayPosition, canvas);

        return canvas;
    }

    createHiDPICanvas(width: number, height: number/*, devicePixelRatio?: number*/): HTMLCanvasElement {

        var canvas = document.createElement("canvas");
        var context = canvas.getContext("2d");

        this.setCanvasDimensions(canvas, context, width, height);

        return canvas;
    }

    setCanvasDimensions(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, width: number, height: number) {
        if (!canvas) {
            return;
        }

        var devicePixelRatio = this.getDevicePixelRatio();
        // 3 ratios seem to misbehave
        if (devicePixelRatio >= 3) {
            devicePixelRatio = 1;
        }

        canvas.width = width * devicePixelRatio;
        canvas.height = height * devicePixelRatio;

        canvas.style.width = width + "px";
        canvas.style.height = height + "px";

        context.scale(devicePixelRatio, devicePixelRatio);
    }

    getDevicePixelRatio(): number {
        return this.$window.devicePixelRatio || 1;
    }

    getPieCanvas(width: number, dataSeries: IPieDataSeries[]): HTMLCanvasElement {
        var canvas = this.createHiDPICanvas(width, width);
        this.drawPieChart(canvas, dataSeries, 9);

        return canvas;
    }

    getPieImage(width: number, dataSeries: IPieDataSeries[]): string {
        var canvas = this.getPieCanvas(width, dataSeries);

        return canvas.toDataURL();
    }

    drawPieChart(canvas: HTMLCanvasElement, data: IPieDataSeries[], padding = 0) {
        var ctx = canvas.getContext("2d");

        var width = canvas.width / this.getDevicePixelRatio();
        var height = canvas.height / this.getDevicePixelRatio();

        var centerX = width / 2;
        var centerY = height / 2;
        var radius = (width / 2) - padding;

        var degrees90 = this.degreeToRadians(90);

        ctx.strokeStyle = "rgba(0,0,0,0.6)";

        var total = this.getTotal(data);

        for (var i = 0; i < data.length; i++) {
            var color = data[i].color;
            var startAngle = this.calculateStart(data, i, total) - degrees90;
            var endAngle = this.calculateEnd(data, i, total) - degrees90;

            ctx.beginPath();
            ctx.fillStyle = color;
            ctx.moveTo(centerX, centerY);
            ctx.arc(centerX, centerY, radius, startAngle, endAngle);
            ctx.fill();

            //var isFullSize = startAngle == 0 && endAngle == this.degreeToRadians(360);
            //if (!isFullSize || true) {
            ctx.stroke();
            //}
            //ctx.rect(canvas.width - 200, y - i * 30, 12, 12);
            //ctx.fill();
            //ctx.font = "13px sans-serif";
            //ctx.fillText(data[i].label + " - " + data[i].value + " (" + calculatePercent(data[i].value, total) + "%)", canvas.width - 200 + 20, y - i * 30 + 10);
        }

        ctx.fillStyle = "white";
        ctx.moveTo(centerX, centerY);
        ctx.beginPath();
        //ctx.arc(x, y, (y - padding) * 0.758, 0, 2 * Math.PI);
        ctx.arc(centerX, centerY, radius * 0.7, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
    }

    calculatePercent(value, total) {
        return ((value / total) * 100).toFixed(2);
    }

    getTotal(data) {
        var sum = 0;
        for (var i = 0; i < data.length; i++) {
            sum += data[i].value;
        }

        return sum;
    }

    calculateStart(data, index, total) {
        if (index === 0) {
            return 0;
        }

        return this.calculateEnd(data, index - 1, total);
    }

    calculateEndAngle(data, index, total) {
        var angle = ((data[index].value / total) * 360);
        var inc = index === 0 ? 0 : this.calculateEndAngle(data, index - 1, total);

        return angle + inc;
    }

    calculateEnd(data, index, total) {
        return this.degreeToRadians(this.calculateEndAngle(data, index, total));
    }

    degreeToRadians(angle) {
        return (angle * Math.PI) / 180;
    }

    getTextWidth(text: string, font: string): number {
        var canvas = this.getSingleUseCanvas(100, 100);
        var context = canvas.getContext("2d");
        context.font = font;

        var metrics = context.measureText(text);

        return metrics.width;
    }

    // ---

    private createCachedImageIdentifier(width: number, height: number, ...identifiers: string[]) {
        const identifier = `${width}-${height}-${identifiers.join("-")}`;
        return identifier;
    }
}
